import { onLogout } from '../../../constants/global';
import { pageurl } from '../../../constants/pageurl';
import type { IActivityDataState, ILogin } from '../../../interface/state/IAuthState';
import { authType } from '../../types';
import type { IResponse } from '../api-utils';
import * as utils from '../utils';

export const login = ({ onFailure, onSuccess, data }: IResponse<ILogin> & { data: { username: string; password: string } }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/login',
			header: utils.header(),
			data,
		},
		actionType: authType.login,
		onSuccess: (res: ILogin) => {
			onSuccess?.(res);
		},
		onFailure,
		auth: true,
	});

export const userFacingLogin = ({ data, onFailure, onSuccess }: IResponse & { data: { email: string } }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/adminlogin',
			header: utils.header(),
			data,
		},
		actionType: authType.userFacingLogin,
		onSuccess: () => {
			onSuccess?.(undefined);
		},
		onFailure,
		auth: true,
	});

export const otpLogin = ({ data, onSuccess, onFailure }: IResponse<ILogin> & { data: { email: string; otp: string } }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/loginwithotp',
			header: utils.header(),
			data,
		},
		actionType: authType.otpLogin,
		onSuccess: (res: ILogin) => {
			onSuccess?.(res);
		},
		onFailure,
		auth: true,
	});

export interface IRegister {
	firstName: string;
	lastName: string;
	email: string;
	password: string;
	phoneNumber: string;
	role: string[];
	partyId?: string;
}

/**
 * `POST /Auth/register` answers `{ message, isSuccessful, statusCode, data: <new user's int id> }`,
 * and `apiRespond` hands the whole body to `onSuccess`. The id was previously thrown away because
 * this was typed as a bare `IResponse` (`onSuccess: (res: undefined) => void`) — which is why the
 * post-create "Assign Election" panel had to guess at the user by scanning a list.
 */
export interface IRegisterResponse {
	message: string;
	isSuccessful: boolean;
	statusCode: number;
	/** The created user's integer primary key — the same value as `IUserState.id`. */
	data: number;
}

export const register = ({ data, onSuccess, onFailure }: IResponse<IRegisterResponse> & { data: IRegister }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/register',
			header: utils.header(),
			data,
		},
		actionType: authType.register,
		onSuccess,
		onFailure,
		auth: true,
	});

export interface IUpdateUser {
	email: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
}

export const update = ({
	data,
	userId,
	onSuccess,
	onFailure,
}: IResponse & {
	data: IUpdateUser;
	userId: string;
}) =>
	utils.httpPutMethod({
		apiData: {
			url: `/api/v1/User/${userId}`,
			header: utils.header(),
			data,
		},
		actionType: authType.update,
		onSuccess,
		onFailure,
		auth: true,
	});

export const changePassword = ({
	data,
	onSuccess,
	onFailure,
}: IResponse & {
	data: {
		currentPassword: string;
		newPassword: string;
	};
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/changepassword',
			header: utils.header(),
			data,
		},
		actionType: authType.changePassword,
		onSuccess,
		onFailure,
		auth: true,
	});

export const logout = ({ onSuccess, onFailure }: IResponse) =>
	utils.httpGetMethod({
		apiData: {
			url: '/api/v1/Auth/logout',
			header: utils.header(),
		},
		actionType: authType.logout,
		onSuccess: (res) => {
			onSuccess?.(res);
			onLogout();
			window.open(pageurl.LOGIN, '_self');
		},
		onFailure: (err) => {
			onFailure?.(err);
			onLogout();
			window.open(pageurl.LOGIN, '_self');
		},
		auth: true,
	});

export const logoutAllDevices = ({ onSuccess, onFailure, data }: IResponse & { data: { email: string } }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/logout/email',
			header: utils.header(),
			data,
		},
		actionType: authType.logoutAllDevices,
		onSuccess,
		onFailure,
		auth: true,
	});

export const activityData = ({ onSuccess, onFailure }: IResponse<IActivityDataState>) =>
	utils.httpGetMethod({
		apiData: {
			url: '/api/v1/Auth/activitydata',
			header: utils.header(),
		},
		actionType: authType.activityData,
		onSuccess,
		onFailure,
		auth: true,
	});
